import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Text, View} from "@khanacademy/wonder-blocks-core";
import {Choice, RadioGroup} from "@khanacademy/wonder-blocks-form";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {doNotTranslateYet} from "@khan/i18n";

import type {
    updateProgramCategory as GqlResponse,
    updateProgramCategoryVariables as GqlVariables,
} from "./__graphql-types__/updateProgramCategory.ts";

export const updateProgramCategoryMutation: GqlOperation<
    GqlResponse,
    GqlVariables
> = gqlOp(gql`
    mutation updateProgramCategory(
        $programId: ID!
        $category: ProgramCategoryInput
    ) {
        updateProgramSettings(
            programId: $programId
            height: 0
            width: 0
            category: $category
        ) {
            error {
                code
                debugMessage
            }
        }
    }
`);

type Props = {
    programId: string;
    category: "regular" | "documentation" | "contest";
};

export default function AdminSettingsButton({programId, category}: Props) {
    const [initialCategory, setInitialCategory] = React.useState(category);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);

    const [updateProgramCategory, result] = useMutation(
        updateProgramCategoryMutation,
    );
    const isSaving = result?.status === "loading";
    const didMutationFail =
        result?.status === "error" ||
        (result?.status === "success" &&
            result.data.updateProgramSettings?.error != null);

    const onSave = async (category) => {
        const result = await updateProgramCategory({
            programId,
            category: mapCategoryToGql(category),
        });
        if (
            result.status !== "success" ||
            result.data.updateProgramSettings?.error != null
        ) {
            return;
        }
        setInitialCategory(category);
        onClose();
    };

    const onClose = isSaving
        ? () => {}
        : () => {
              setIsModalOpen(false);
              buttonRef.current?.focus();
          };

    return (
        <>
            <Button
                ref={buttonRef}
                kind="secondary"
                onClick={() => setIsModalOpen(true)}
            >
                {doNotTranslateYet`Settings`}
            </Button>
            <ModalLauncher
                opened={isModalOpen}
                onClose={onClose}
                modal={
                    <SetCategoryModal
                        initialCategory={initialCategory}
                        onSave={onSave}
                        isSaving={isSaving}
                        didMutationFail={didMutationFail}
                        onClose={onClose}
                    />
                }
            />
        </>
    );
}

function SetCategoryModal({
    initialCategory,
    onSave,
    isSaving,
    didMutationFail,
    onClose,
}: {
    initialCategory: Props["category"];
    onSave: (settings: Props["category"]) => void;
    isSaving: boolean;
    didMutationFail: boolean;
    onClose: () => void;
}): React.ReactElement {
    const [category, setCategory] = React.useState(initialCategory);

    return (
        <OnePaneDialog
            title={doNotTranslateYet`Settings`}
            content={
                <>
                    <TypeSafeRadioGroup
                        groupName="python-user-program-category"
                        disabled={isSaving}
                        label={
                            <Text style={styles.sectionHeading}>
                                {doNotTranslateYet`Category`}
                            </Text>
                        }
                        onChange={(value) => setCategory(value)}
                        selectedValue={category}
                        choices={[
                            {
                                label: doNotTranslateYet`Regular`,
                                value: "regular",
                            },
                            {
                                label: doNotTranslateYet`Documentation`,
                                value: "documentation",
                            },
                            {
                                label: doNotTranslateYet`Official Contest`,
                                value: "contest",
                            },
                        ]}
                    />
                    {didMutationFail && (
                        <LabelMedium style={styles.error} role="alert">
                            {doNotTranslateYet`We could not save these changes. Please try again.`}
                        </LabelMedium>
                    )}
                </>
            }
            footer={
                <View style={styles.footer}>
                    <Button
                        kind="tertiary"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        {doNotTranslateYet`Cancel`}
                    </Button>
                    <Button
                        onClick={() => onSave(category)}
                        disabled={category === initialCategory}
                        spinner={isSaving}
                    >
                        {doNotTranslateYet`Save and Close`}
                    </Button>
                </View>
            }
        />
    );
}

/**
 * A wrapper to ensure that only expected values can be passed to onChange,
 * to avoid having to explicitly refine the type inside onChange.
 */
function TypeSafeRadioGroup<T extends string>({
    groupName,
    label,
    onChange,
    selectedValue,
    choices,
    disabled,
}: {
    groupName: string;
    label: React.ReactElement;
    onChange: (value: T) => void;
    selectedValue: T;
    choices: Array<{label: string; value: T}>;
    disabled?: boolean;
}): React.ReactElement {
    return (
        <RadioGroup
            groupName={groupName}
            label={label}
            onChange={(value) => onChange(value as T)}
            selectedValue={selectedValue}
        >
            {choices.map((choice) => (
                <Choice
                    key={choice.value}
                    label={choice.label}
                    value={choice.value}
                    disabled={disabled}
                />
            ))}
        </RadioGroup>
    );
}

function mapCategoryToGql(
    category: Props["category"],
): GqlVariables["category"] {
    switch (category) {
        case "regular":
            return null;
        case "documentation":
            return "DOCUMENTATION";
        case "contest":
            return "OFFICIAL";
    }
}

const styles = StyleSheet.create({
    sectionHeading: {
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: "bold",
    },
    error: {
        marginTop: spacing.small_12,
        color: "red",
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.medium_16,
    },
});
